<template>
  <div class="brain-search" :class="{ 'brain-search--full': props.fullWidth }">
 

    <div class="search-container">
      <v-card class="search-card" elevation="2">
        <div
          v-if="isSearching || isSearchingAsync"
          class="search-card-overlay"
        >
          <div class="overlay-content">
            <v-progress-circular
              indeterminate
              color="#EA5545"
              size="70"
            ></v-progress-circular>
            <p class="overlay-text">
              {{ isSearchingAsync ? 'Clearment.AI Suche läuft... Bitte warten.' : t('brainComponents.common.overlayText') }}
            </p>
          </div>
        </div>
        <v-card-text>
          <!-- Product Selection -->
          <div
            v-if="userProducts.length > 0 && !props.lockProductSelection"
            class="product-selector mb-4"
          >
            <label class="product-label">
              <v-icon size="16" color="#EA5545" class="mr-2">mdi-package</v-icon>
              {{ t('brainComponents.search.productSelectorLabel') }}
              <span class="required-star">*</span>
            </label>
            <v-select
              v-model="selectedProductId"
              :items="productOptions"
              item-title="label"
              item-value="value"
              variant="outlined"
              density="comfortable"
              :disabled="isSearching || isSearchingAsync"
              :placeholder="t('brainComponents.search.productSelectorPlaceholder')"
            >
              <template v-slot:prepend-inner>
                <v-icon size="18" color="#EA5545">mdi-package</v-icon>
              </template>
            </v-select>
          </div>

          <!-- Aktuell ausgewähltes Produkt -->
          <div v-if="currentProduct" class="current-product-info mb-4">
            <div class="current-product-heading">
              <v-icon size="22" color="#EA5545" class="mr-2">mdi-package-variant</v-icon>
              <div class="current-product-text">
                <div class="current-product-title">{{ currentProduct.prod_name }}</div>
                <div
                  class="current-product-category"
                  v-if="currentProduct.prod_category !== undefined && currentProduct.prod_category !== null"
                >
             {{ formatProductCategory(currentProduct.prod_category) }}
                </div>
                <div
                  class="current-product-category"
                  v-else
                >
                  {{ t('brainComponents.search.categoryUnknown') }}
                </div>
              </div>
            </div>
          
          </div>

          <v-textarea
            v-model="searchBrainText"
            :label="t('brainComponents.search.textareaLabel')"
            :placeholder="t('brainComponents.search.textareaPlaceholder')"
            rows="4"
            variant="outlined"
            class="mb-4"
            :disabled="isSearching || isSearchingAsync || (requiresProductSelection && !resolvedProductId)"
          ></v-textarea>

          <div class="search-actions">
            <v-btn
              v-if="!showAsyncButton"
              @click="searchBrain"
              color="#EA5545"
              size="large"
              :loading="isSearching"
              :disabled="!searchBrainText.trim() || isSearching || isSearchingAsync || (requiresProductSelection && !resolvedProductId)"
              class="search-btn"
            >
              <v-icon left class="mr-2">mdi-brain</v-icon>
              {{ t('brainComponents.search.actionButton') }} ({{ currentProduct?.prod_name || 'N/A' }} - Tarif: {{ currentProduct?.prod_klv_id || 'N/A' }})
            </v-btn>
            <v-btn
            v-if="showAsyncButton"
              @click="searchBrainAsync"
              color="#EA5545"
              size="large"
              :loading="isSearchingAsync"
              :disabled="!searchBrainText.trim() || isSearching || isSearchingAsync || (requiresProductSelection && !resolvedProductId)"
              class="search-btn"
            >
              <v-icon left class="mr-2">mdi-brain</v-icon>
              {{ t('brainComponents.search.actionButton') }} ({{ currentProduct?.prod_name || 'N/A' }} - Tarif: {{ currentProduct?.prod_klv_id || 'N/A' }})
            </v-btn>
          </div>

          <!-- Response -->
          <div v-if="brainResponse && !isSearching" class="response-container">
            <div class="response-header">
              <v-icon color="#EA5545" class="mr-2" size="24">mdi-check-circle</v-icon>
              <span class="response-title">{{ t('brainComponents.search.responseTitle') }}</span>
            </div>
            <div class="response-content">
              <p class="response-text">{{ brainResponse }}</p>
            </div>
            <v-btn
              variant="text"
              color="#EA5545"
              @click="clearSearch"
              class="mt-2"
              size="small"
            >
              <v-icon left size="18">mdi-refresh</v-icon>
              {{ t('brainComponents.search.responseReset') }}
            </v-btn>
          </div>

          <!-- Error -->
          <div v-if="brainError && !isSearching" class="error-container">
            <div class="error-header">
              <v-icon color="error" class="mr-2" size="24">mdi-alert-circle</v-icon>
              <span class="error-title">{{ t('brainComponents.search.errorTitle') }}</span>
            </div>
            <div class="error-content">
              {{ brainError }}
            </div>
            <v-btn
              variant="text"
              color="error"
              @click="clearSearch"
              class="mt-2"
              size="small"
            >
              <v-icon left size="18">mdi-refresh</v-icon>
              {{ t('brainComponents.search.errorReset') }}
            </v-btn>
          </div>
        </v-card-text>
      </v-card>

      <!-- Search History -->
      <v-card v-if="showHistory" class="history-card mt-4" elevation="1">
        <v-card-title class="history-header" @click="isHistoryExpanded = !isHistoryExpanded">
          <div class="history-title-row">
            <v-icon size="20" class="mr-2" color="#EA5545">mdi-history</v-icon>
            <span class="history-title">{{ t('brainComponents.search.historyTitle') }}</span>
            <v-chip size="x-small" color="#EA5545" v-if="searchHistory.length > 0">
              {{ searchHistory.length }}
            </v-chip>
          </div>
          <v-icon>{{ isHistoryExpanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
        </v-card-title>

        <v-expand-transition>
          <div v-show="isHistoryExpanded">
            <v-divider></v-divider>
            <v-card-text>
              <!-- Loading State -->
              <div v-if="isLoadingHistory" class="loading-state-small">
                <v-progress-circular indeterminate color="#EA5545" size="24"></v-progress-circular>
                <p class="state-text-small">{{ t('brainComponents.search.historyLoading') }}</p>
              </div>

              <!-- History List -->
              <div v-else-if="searchHistory.length > 0" class="history-list">
                <div
                  v-for="request in searchHistory"
                  :key="request.breq_id"
                  class="history-item"
                  @click="loadHistoryItem(request)"
                >
                  <div class="history-item-header">
                    <v-icon 
                      :color="request.breq_status === 'success' ? '#4caf50' : '#d32f2f'" 
                      size="16" 
                      class="mr-2"
                    >
                      {{ request.breq_status === 'success' ? 'mdi-check-circle' : 'mdi-alert-circle' }}
                    </v-icon>
                    <span class="history-date">{{ formatHistoryDate(request.breq_created_date) }}</span>
                    <v-chip 
                      v-if="request.breq_product_id > 0" 
                      size="x-small" 
                      color="#EA5545"
                      class="ml-2"
                    >
                      <v-icon size="12" class="mr-1">mdi-package</v-icon>
                      {{ t('brainComponents.search.historyProductChip') }}
                    </v-chip>
                  </div>
                  <p class="history-prompt">{{ truncateText(request.breq_prompt, 80) }}</p>
                  <div v-if="request.breq_total_cost > 0" class="history-cost">
                    {{ formatHistoryCost(request.breq_total_cost, request.breq_total_tokens) }}
                  </div>
                </div>
              </div>

              <!-- Empty State -->
              <div v-else class="state-block" data-state="empty" data-size="sm">
                <v-icon size="40" color="#ccc">mdi-history</v-icon>
                <p class="state-text-small">{{ t('brainComponents.search.historyEmpty') }}</p>
              </div>
            </v-card-text>
          </div>
        </v-expand-transition>
      </v-card>
    </div>
  </div>
</template>

<script setup>
import { ref, inject, onMounted, onBeforeUnmount, computed, defineProps, defineEmits, watch } from 'vue'
import { useLanguage } from '@/composables/useLanguage'

// Props
const props = defineProps({
  showHistory: {
    type: Boolean,
    default: false
  },
  // Optional: Produkt für diese Suche vorgeben (z.B. aus Vertrag-Detail)
  presetProductId: {
    type: [Number, String],
    default: null
  },
  presetQuestion: {
    type: String,
    default: ''
  },
  // Wenn true: Produkt-Auswahl wird versteckt und fest auf presetProductId gesetzt
  lockProductSelection: {
    type: Boolean,
    default: false
  },
  fullWidth: {
    type: Boolean,
    default: false
  }
})

// Emits
const emit = defineEmits(['search-completed'])

const baseCalls = inject('baseCalls')
const { t, languageStore } = useLanguage()

// Brain Search State
const searchBrainText = ref('')
const brainResponse = ref('')
const brainError = ref('')
const isSearching = ref(false)
const isSearchingAsync = ref(false)
const asyncStatus = ref(null)
const pollingInterval = ref(null)
const showAsyncButton = ref(true)

// History State
const searchHistory = ref([])
const isLoadingHistory = ref(false)
const isHistoryExpanded = ref(false)

// Product State
const userProducts = ref([])
const selectedProductId = ref(null)

// Computed
const productOptions = computed(() => {
  const options = []
  
  userProducts.value.forEach(product => {
    options.push({
      label: product.prod_name,
      value: product.myp_product_id
    })
  })
  
  return options
})

const currentProduct = computed(() => {
  const id = selectedProductId.value || props.presetProductId
  if (!id && id !== 0) return null
  const numId = Number(id)
  if (Number.isNaN(numId)) return null
  return userProducts.value.find(p => Number(p.myp_product_id) === numId) || null
})

const resolvedProductId = computed(() => {
  if (selectedProductId.value || selectedProductId.value === 0) {
    return selectedProductId.value
  }
  if (props.presetProductId || props.presetProductId === 0) {
    return props.presetProductId
  }
  return null
})

const requiresProductSelection = computed(() => {
  return !props.lockProductSelection && userProducts.value.length > 0
})

async function searchBrain() {
  if (!searchBrainText.value.trim()) return
  
  isSearching.value = true
  brainResponse.value = ''
  brainError.value = ''
  
  try {
    const tarifId = Number(currentProduct.value?.prod_klv_id ?? 0) || 0
    const productName = currentProduct.value?.prod_name || 'Unknown Product'
    
    console.log('=== Brain Search Debug ===')
    console.log('Tarif ID (prod_klv_id):', tarifId)
    console.log('Product Name:', productName)
    console.log('currentProduct:', currentProduct.value)
    
    const response = await baseCalls.clearmentPost({
      action: 'searchOpenaiVectorStoreTarife',
      prompt: searchBrainText.value,
      tarif_id: tarifId,
      product_id: 0, // Not using product_id anymore, using tarif_id directly
      product_name: productName,
      product_category: currentProduct.value?.prod_category ?? null
    })
    
    if (response.data.success) {
      brainResponse.value = response.data.result || response.data.payload
      // Reload history to show new request
      await loadSearchHistory()
      // Emit event for parent component
      emit('search-completed', {
        requestId: response.data.request_id || null,
        tarifId: tarifId,
        prompt: searchBrainText.value,
        result: brainResponse.value,
        prodCategory: currentProduct.value?.prod_category ?? null
      })
    } else {
      brainError.value = response.data.error || t('brainComponents.search.errors.generic')
    }
  } catch (error) {
    brainError.value = t('brainComponents.search.errors.network', { message: error.message })
  } finally {
    isSearching.value = false
  }
}

async function searchBrainAsync() {
  if (!searchBrainText.value.trim()) return
  
  isSearchingAsync.value = true
  brainResponse.value = ''
  brainError.value = ''
  asyncStatus.value = null
  
  // Clear any existing polling
  if (pollingInterval.value) {
    if (typeof pollingInterval.value === 'number') {
      clearTimeout(pollingInterval.value)
    }
    pollingInterval.value = null
  }
  
  try {
    const tarifId = Number(currentProduct.value?.prod_klv_id ?? 0) || 0
    const productName = currentProduct.value?.prod_name || 'Unknown Product'
    
    console.log('=== Brain Search Async Debug ===')
    console.log('Tarif ID (prod_klv_id):', tarifId)
    console.log('Product Name:', productName)
    
    const response = await baseCalls.clearmentPost({
      action: 'searchOpenaiVectorStoreTarifeAsync',
      prompt: searchBrainText.value,
      tarif_id: tarifId,
      product_id: 0, // Not using product_id anymore
      product_name: productName,
      product_category: currentProduct.value?.prod_category ?? null
    })
    
    if (response.data.success && response.data.run_id && response.data.thread_id) {
      asyncStatus.value = {
        status: response.data.status,
        run_id: response.data.run_id,
        thread_id: response.data.thread_id,
        request_id: response.data.request_id
      }
      
      // Start polling for status
      startPolling(response.data.run_id, response.data.thread_id, tarifId)
      
    } else {
      brainError.value = response.data.error || 'Async-Suche fehlgeschlagen'
      isSearchingAsync.value = false
    }
  } catch (error) {
    brainError.value = `Fehler bei Async-Suche: ${error.message}`
    isSearchingAsync.value = false
  }
}

function startPolling(runId, threadId, tarifId) {
  // Use recursive setTimeout instead of setInterval to ensure
  // we wait for the response before scheduling the next check
  
  const checkStatus = async () => {
    // Safety check: if polling was cancelled, stop
    if (!pollingInterval.value) {
      return
    }
    
    try {
      const statusResponse = await baseCalls.clearmentPost({
        action: 'searchOpenaiVectorStoreTarifeStatus',
        run_id: runId,
        thread_id: threadId
      })
      
      const currentStatus = statusResponse.data.status
      const hasResult = statusResponse.data.result && String(statusResponse.data.result).trim().length > 0
      
      // Stop polling immediately when result is available
      if (statusResponse.data.success && hasResult && currentStatus === 'completed') {
        // Immediately stop polling
        pollingInterval.value = null
        isSearchingAsync.value = false
        
        // Set result
        brainResponse.value = statusResponse.data.result
        asyncStatus.value = {
          ...asyncStatus.value,
          status: 'completed'
        }
        
        // Reload history
        await loadSearchHistory()
        
        // Emit event
        emit('search-completed', {
          requestId: asyncStatus.value.request_id || null,
          tarifId: tarifId,
          prompt: searchBrainText.value,
          result: brainResponse.value,
          prodCategory: currentProduct.value?.prod_category ?? null,
          async: true,
          asyncData: asyncStatus.value
        })
        
        return // Exit function immediately
      }
      
      // Stop on error
      if (currentStatus === 'error' || statusResponse.data.error) {
        pollingInterval.value = null
        isSearchingAsync.value = false
        
        brainError.value = statusResponse.data.error || 'Async-Suche fehlgeschlagen'
        return // Exit immediately
      }
      
      // Still pending - schedule next check after 2 seconds
      if (pollingInterval.value) {
        pollingInterval.value = setTimeout(checkStatus, 2000)
      }
      
    } catch (error) {
      console.error('Polling error:', error)
      
      pollingInterval.value = null
      isSearchingAsync.value = false
      
      brainError.value = `Fehler beim Abrufen des Status: ${error.message}`
    }
  }
  
  // Start first check immediately
  pollingInterval.value = 'active' // Set a truthy value to indicate polling is active
  checkStatus()
}

function clearSearch() {
  searchBrainText.value = ''
  brainResponse.value = ''
  brainError.value = ''
  asyncStatus.value = null
  
  // Stop any active polling
  if (pollingInterval.value) {
    if (typeof pollingInterval.value === 'number') {
      clearTimeout(pollingInterval.value)
    }
    pollingInterval.value = null
  }
  isSearchingAsync.value = false
  
  // Bei gelockter Produktauswahl nicht zurücksetzen
  if (!props.lockProductSelection) {
    selectedProductId.value = null
  }
}

async function loadSearchHistory() {
  if (!props.showHistory) return
  
  isLoadingHistory.value = true
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getmybrainrequests'
    })
    
    if (response.data.success && response.data.requests) {
      searchHistory.value = response.data.requests
    }
  } catch (error) {
    console.error('Error loading search history:', error)
  } finally {
    isLoadingHistory.value = false
  }
}

function loadHistoryItem(request) {
  searchBrainText.value = request.breq_prompt
  selectedProductId.value = request.breq_product_id || 0
  
  if (request.breq_status === 'success' && request.breq_result) {
    brainResponse.value = request.breq_result
    brainError.value = ''
  } else {
    brainError.value = request.breq_error || t('brainComponents.search.errors.requestFailed')
    brainResponse.value = ''
  }
  
  // Scroll to top to see the loaded response
  window.scrollTo({ top: 0, behavior: 'smooth' })
}

async function loadUserProducts() {
  try {
    const response = await baseCalls.clearmentPost({
      action: 'getmyproducts'
    })
    
    if (response.data.success && response.data.products) {
      userProducts.value = response.data.products

      // Falls ein Produkt vorgegeben ist, nachladen und setzen
      if (props.presetProductId) {
        const preset = Number(props.presetProductId)
        const exists = userProducts.value.some(
          p => Number(p.myp_product_id) === preset
        )
        if (exists) {
          selectedProductId.value = preset
        }
      }
    }
  } catch (error) {
    console.error('Error loading products:', error)
  }
}

function formatHistoryDate(dateString) {
  if (!dateString || dateString === '1973-01-01 00:00:00') return t('brainComponents.search.historyUnknownDate')
  
  const date = new Date(dateString)
  const now = new Date()
  const diffInMs = now - date
  const diffInMinutes = Math.floor(diffInMs / (1000 * 60))
  const diffInHours = Math.floor(diffInMs / (1000 * 60 * 60))
  const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24))

  if (diffInMinutes < 1) return t('brainComponents.search.historyRelative.justNow')
  if (diffInMinutes < 60) return t('brainComponents.search.historyRelative.minutes', { value: diffInMinutes })
  if (diffInHours < 24) return t('brainComponents.search.historyRelative.hours', { value: diffInHours })
  if (diffInDays < 7) return t('brainComponents.search.historyRelative.days', { value: diffInDays })
  
  return date.toLocaleDateString(getCurrentLocale(), { 
    month: 'short', 
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  })
}

function formatHistoryCost(cost = 0, tokens = 0) {
  const safeCost = Number(cost) || 0
  const safeTokens = Number(tokens) || 0
  return t('brainComponents.search.historyCost', {
    cost: safeCost.toFixed(6),
    tokens: safeTokens
  })
}

function truncateText(text, maxLength) {
  if (!text) return ''
  if (text.length <= maxLength) return text
  return text.substring(0, maxLength) + '...'
}

function formatProductCategory(category) {

  const num = Number(category)

    if (num === 1) {
      return 'Zahnzusatzversicherung'
    } else if (num === 2) {
      return 'Tierkrankenversicherung'
    }  

}

function getCurrentLocale() {
  const lang = languageStore?.currentLanguage?.value || 'de'
  return lang === 'de' ? 'de-DE' : 'en-US'
}

onMounted(() => {
  loadSearchHistory()
  loadUserProducts()
})

onBeforeUnmount(() => {
  // Stop polling when component is destroyed
  if (pollingInterval.value) {
    if (typeof pollingInterval.value === 'number') {
      clearTimeout(pollingInterval.value)
    }
    pollingInterval.value = null
  }
})

watch(
  () => props.presetProductId,
  (newVal) => {
    if (props.lockProductSelection && (newVal || newVal === 0)) {
      selectedProductId.value = Number(newVal)
    }
  },
  { immediate: true }
)

watch(
  () => props.presetQuestion,
  (newQuestion) => {
    if (typeof newQuestion === 'string') {
      searchBrainText.value = newQuestion
    } else if (newQuestion === null || newQuestion === undefined) {
      searchBrainText.value = ''
    }
  },
  { immediate: true }
)
</script>

<style scoped src="@/assets/styles/sfc/components/objects/BrainSearch.css"></style>
